import { useCallback, useRef, useState } from "react";

// Matches the backend cap (PsnsRedriveService.PSNS_REDRIVE_MAX_EVENTS).
export const PSNS_REDRIVE_MAX_EVENTS = 10_000;

// Counts events in an NDJSON file: one EventBridge envelope (a JSON object) per
// non-blank line. Field-level checks (Source, DetailType, Detail, Time) happen
// in the publisher and show up as INVALID in the dry run.
export const countNdjsonEvents = (text: string): number => {
  let count = 0;
  const lines = text.split("\n");
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index].trim();
    if (line) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(line);
      } catch {
        throw new Error(`Line ${index + 1} is not valid JSON`);
      }
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error(`Line ${index + 1} is not a JSON object`);
      }
      count += 1;
      if (count > PSNS_REDRIVE_MAX_EVENTS) {
        throw new Error(
          `File has more than ${PSNS_REDRIVE_MAX_EVENTS.toLocaleString()} events; split it into smaller files`
        );
      }
    }
  }
  if (count === 0) {
    throw new Error("File contains no events");
  }
  return count;
};

export const usePsnsFileValidation = () => {
  const [file, setFile] = useState<File>();
  const [eventCount, setEventCount] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [validating, setValidating] = useState(false);
  const [fileOK, setFileOK] = useState(false);
  const validationSequence = useRef(0);

  const handleFileChange = useCallback(async (files: File[]): Promise<void> => {
    const validationId = validationSequence.current + 1;
    validationSequence.current = validationId;
    const selectedFile = files[0];

    setFile(selectedFile);
    setEventCount(0);
    setError("");
    setFileOK(false);

    if (!selectedFile) {
      setValidating(false);
      return;
    }

    setValidating(true);
    try {
      const count = countNdjsonEvents(await selectedFile.text());
      if (validationSequence.current !== validationId) {
        return;
      }
      setEventCount(count);
      setFileOK(true);
    } catch (err) {
      if (validationSequence.current !== validationId) {
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (validationSequence.current === validationId) {
        setValidating(false);
      }
    }
  }, []);

  return { file, eventCount, error, validating, fileOK, handleFileChange };
};
